/**
 * The selected platform project.
 *
 * A store rather than a route param or a page-local ref, for one concrete
 * reason: the flow list and the editor must agree on it. A project chosen in
 * the list and then forgotten on the way into the editor would put a flow's
 * variable pickers in the wrong plant — and a flow that reads "Faena Sur"
 * variables while its author believed they were in "Planta Norte" is exactly
 * the mistake that costs a day to find.
 *
 * The choice is persisted, because it is a working context rather than a
 * filter: reopening the tool should resume where the author was. It is NOT in
 * the URL — no route declares a project query — so `localStorage` is the whole
 * of the round trip, including for the "every project" choice.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { fetchProjects } from '../../../api/platform.js'
import type { Project } from '../../../types/platform.types.js'

const STORAGE_KEY = 'vulcan.selectedProjectId'

/**
 * The "every project" choice. A string sentinel rather than `null`, because
 * `null` already means "nothing chosen yet" (cold start, or a user with no
 * projects) and the two must not collapse: one shows the flow list empty with
 * a "choose a project" prompt, the other lists every flow there is.
 */
export const ALL_PROJECTS = 'all'

export type ProjectSelection = number | typeof ALL_PROJECTS | null

/**
 * Reads back what `select` wrote into `localStorage`.
 *
 * A stored id that no longer exists (project archived, or a laptop pointed at
 * a different stage) must not leave the app filtering by a phantom project, so
 * ids are validated against the loaded list and anything else answers `null`,
 * which the caller turns into its own fallback. `ALL_PROJECTS` needs no such
 * check: it does not name a row.
 */
export function parseStoredSelection(
  raw: string | null,
  projectIds: readonly number[],
): ProjectSelection {
  if (raw === ALL_PROJECTS) return ALL_PROJECTS
  if (raw === null || raw.trim() === '') return null
  const id = Number(raw)
  return Number.isInteger(id) && projectIds.includes(id) ? id : null
}

export const useProjectStore = defineStore('projects', () => {
  const projects = ref<Project[]>([])
  const selection = ref<ProjectSelection>(null)
  const loading = ref(false)
  const loaded = ref(false)
  const error = ref(false)

  /**
   * The one concrete project, or `null` under "every project" as well as under
   * "nothing chosen". Everything that writes — creating a flow, importing one,
   * the editor's project label — needs exactly one project and already treats
   * `null` as "cannot proceed", so the sentinel never reaches them.
   */
  const selectedId = computed<number | null>(() =>
    typeof selection.value === 'number' ? selection.value : null,
  )

  const isAllProjects = computed(() => selection.value === ALL_PROJECTS)

  const selected = computed(
    () => projects.value.find((project) => project.id === selectedId.value) ?? null,
  )

  function select(value: ProjectSelection): void {
    selection.value = value
    if (value === null) window.localStorage.removeItem(STORAGE_KEY)
    else window.localStorage.setItem(STORAGE_KEY, String(value))
  }

  async function load(): Promise<void> {
    if (loading.value) return
    loading.value = true
    error.value = false
    try {
      projects.value = await fetchProjects()

      const restored = parseStoredSelection(
        window.localStorage.getItem(STORAGE_KEY),
        projects.value.map((project) => project.id),
      )
      if (restored !== null) selection.value = restored
      else select(projects.value[0]?.id ?? null)

      loaded.value = true
    } catch {
      error.value = true
    } finally {
      loading.value = false
    }
  }

  return {
    projects,
    selection,
    selectedId,
    isAllProjects,
    selected,
    loading,
    loaded,
    error,
    select,
    load,
  }
})
